/**
 * Templates (KPI library) + Targets (per-node per-period scorecards)
 * Replaces the external scorecard-builder /scorecards page.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Plus, Edit2, Trash2, RefreshCw, Copy, ChevronRight, Search,
  Library, Calendar, AlertCircle, Wand2, Save, X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERSPECTIVE_OPTIONS = ['FINANCIAL', 'CUSTOMER', 'INTERNAL_PROCESS', 'LEARNING'];
const CATEGORY_OPTIONS = ['REVENUE', 'SALES', 'MARKETING', 'CREATIVE', 'OPERATION', 'PRODUCT', 'SIGNAL', 'CUSTOMER', 'EDUCATION', 'GROWTH', 'OTHER'];
const DIRECTION_OPTIONS = ['HIGH', 'LOW'];
const ROLLUP_OPTIONS = ['SUM', 'AVG'];

// ============================================================================
// TEMPLATES MANAGEMENT
// ============================================================================

function TemplateModal({ open, mode, template, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(mode === 'add' ? {
      code: '', name: '', perspective: 'INTERNAL_PROCESS', category: 'OTHER',
      unit: '', direction: 'HIGH', rollup: 'SUM', sort_order: 0, active: true
    } : { ...template });
  }, [open, mode, template]);

  const save = async () => {
    if (!form.code?.trim() || !form.name?.trim()) {
      toast.error('Code dan Name required');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const h = { Authorization: `Bearer ${token}` };
      if (mode === 'add') {
        await axios.post(`${API}/scorecard/native/template`, form, { headers: h });
        toast.success('Template created');
      } else {
        await axios.put(`${API}/scorecard/native/template/${template.id}`, form, { headers: h });
        toast.success('Template updated');
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-md" data-testid="template-edit-modal">
        <DialogHeader>
          <DialogTitle className="text-white">{mode === 'add' ? 'Add Template' : 'Edit Template'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-[#9CA3AF] mb-1 block">Code * (e.g. REV)</label>
              <Input value={form.code || ''} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                data-testid="template-code-input"
                className="bg-transparent border-[hsl(var(--border))] text-white font-mono" />
            </div>
            <div>
              <label className="text-xs text-[#9CA3AF] mb-1 block">Unit (e.g. RM, %, count)</label>
              <Input value={form.unit || ''} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                className="bg-transparent border-[hsl(var(--border))] text-white" />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Name *</label>
            <Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="template-name-input"
              className="bg-transparent border-[hsl(var(--border))] text-white" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-[#9CA3AF] mb-1 block">Perspective</label>
              <select value={form.perspective || ''} onChange={(e) => setForm({ ...form, perspective: e.target.value })}
                className="w-full bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white text-sm rounded-md px-2 py-2">
                {PERSPECTIVE_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#9CA3AF] mb-1 block">Category</label>
              <select value={form.category || ''} onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="template-category-select"
                className="w-full bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white text-sm rounded-md px-2 py-2">
                {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs text-[#9CA3AF] mb-1 block">Direction</label>
              <select value={form.direction || 'HIGH'} onChange={(e) => setForm({ ...form, direction: e.target.value })}
                className="w-full bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white text-sm rounded-md px-2 py-2">
                {DIRECTION_OPTIONS.map(d => <option key={d} value={d}>{d === 'HIGH' ? 'High = better' : 'Low = better'}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#9CA3AF] mb-1 block">Rollup</label>
              <select value={form.rollup || 'SUM'} onChange={(e) => setForm({ ...form, rollup: e.target.value })}
                className="w-full bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white text-sm rounded-md px-2 py-2">
                {ROLLUP_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-[#9CA3AF] mb-1 block">Sort</label>
              <Input type="number" value={form.sort_order || 0} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
                className="bg-transparent border-[hsl(var(--border))] text-white" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="template-save-btn"
            className="bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white">{saving ? 'Saving...' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState({ open: false, mode: 'add', template: null });
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');
  const h = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/scorecard/native/templates`, { headers: h });
      setTemplates(r.data?.templates || []);
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await axios.delete(`${API}/scorecard/native/template/${t.id}`, { headers: h });
      toast.success('Deleted');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const filtered = templates.filter(t => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (t.code || '').toLowerCase().includes(s) || (t.name || '').toLowerCase().includes(s) || (t.category || '').toLowerCase().includes(s);
  });

  return (
    <div className="space-y-3" data-testid="templates-tab">
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Library size={16} className="text-[#10B981]" />
          <div>
            <h3 className="text-white font-semibold text-sm">KPI Templates</h3>
            <p className="text-[10px] text-[#6B7280]">{templates.length} metrics defined</p>
          </div>
        </div>
        <Button onClick={() => setModal({ open: true, mode: 'add', template: null })}
          size="sm" data-testid="template-add-btn"
          className="bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white"><Plus size={14} className="mr-1" />Add Template</Button>
      </div>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by code, name, category..."
          className="pl-9 bg-transparent border-[hsl(var(--border))] text-white text-sm h-9" />
      </div>
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--surface-elevated))] text-[10px] text-[#9CA3AF] uppercase">
            <tr>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Perspective</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-left px-3 py-2">Unit</th>
              <th className="text-center px-3 py-2">Dir</th>
              <th className="text-center px-3 py-2">Roll</th>
              <th className="text-right px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(t => (
              <tr key={t.id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-elevated))]/40 group"
                data-testid={`template-row-${t.id}`}>
                <td className="px-3 py-2 font-mono text-xs text-white">{t.code}</td>
                <td className="px-3 py-2 text-white text-xs">{t.name}</td>
                <td className="px-3 py-2 text-xs text-[#9CA3AF]">{t.perspective}</td>
                <td className="px-3 py-2 text-xs"><Badge className="bg-[#3B82F6]/15 text-[#3B82F6] text-[10px] border-[#3B82F6]/30">{t.category}</Badge></td>
                <td className="px-3 py-2 text-xs text-[#9CA3AF]">{t.unit || '—'}</td>
                <td className="px-3 py-2 text-center text-xs">{t.direction === 'HIGH' ? '↑' : '↓'}</td>
                <td className="px-3 py-2 text-center text-xs">{t.rollup}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition">
                    <button onClick={() => setModal({ open: true, mode: 'edit', template: t })}
                      data-testid={`template-edit-${t.id}`}
                      className="p-1.5 hover:bg-[#3B82F6]/15 rounded text-[#3B82F6]"><Edit2 size={12} /></button>
                    <button onClick={() => handleDelete(t)} data-testid={`template-delete-${t.id}`}
                      className="p-1.5 hover:bg-[#E63946]/15 rounded text-[#E63946]"><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-[#6B7280] text-xs">No templates.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <TemplateModal {...modal} onClose={() => setModal({ open: false, mode: 'add', template: null })} onSaved={load} />
    </div>
  );
}

// ============================================================================
// TARGETS (Scorecards page equivalent)
// ============================================================================

export function TargetsTab() {
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [nodes, setNodes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [editingTargets, setEditingTargets] = useState({});
  const [saving, setSaving] = useState(false);
  const [copyFrom, setCopyFrom] = useState('');
  const [duplicateNodeState, setDuplicateNodeState] = useState({ open: false, node: null });
  const [nodeSearch, setNodeSearch] = useState(''); // Pick Node search filter
  const token = localStorage.getItem('token');
  const h = { Authorization: `Bearer ${token}` };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [tr, nr, er] = await Promise.all([
        axios.get(`${API}/scorecard/native/templates`, { headers: h }),
        axios.get(`${API}/scorecard/native/org-tree`, { headers: h }),
        axios.get(`${API}/scorecard/native/entries?period=${period}`, { headers: h }),
      ]);
      setTemplates(tr.data?.templates || []);
      setNodes(nr.data?.nodes || []);
      setEntries(er.data?.entries || []);
      setEditingTargets({});
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [period]);

  // Build node tree (display only IB and STAFF — the levels where targets are set)
  const sortedNodes = useMemo(() => {
    const sorted = [...nodes].sort((a, b) => {
      const order = { COMPANY: 0, PM: 1, IB: 2, STAFF: 3 };
      return (order[a.type] || 9) - (order[b.type] || 9) || a.name.localeCompare(b.name);
    });
    return sorted;
  }, [nodes]);

  // Group entries by node for fast lookup
  const entryMap = useMemo(() => {
    const m = {};
    entries.forEach(e => {
      m[`${e.node_id}|${e.template_id}`] = e;
    });
    return m;
  }, [entries]);

  const selectedNode = nodes.find(n => n.id === selectedNodeId);

  const getCurrentTarget = (template_id) => {
    if (!selectedNodeId) return 0;
    const key = `${selectedNodeId}|${template_id}`;
    if (editingTargets[key] !== undefined) return editingTargets[key];
    return entryMap[key]?.target || 0;
  };

  const setTarget = (template_id, value) => {
    setEditingTargets(prev => ({ ...prev, [`${selectedNodeId}|${template_id}`]: value }));
  };

  const dirtyCount = Object.keys(editingTargets).length;

  const saveDirty = async () => {
    if (dirtyCount === 0) return;
    setSaving(true);
    try {
      const items = Object.entries(editingTargets).map(([key, target]) => {
        const [node_id, template_id] = key.split('|');
        return { node_id, template_id, period, target: Number(target) || 0 };
      });
      const r = await axios.post(`${API}/scorecard/native/entries/bulk-upsert`, { entries: items }, { headers: h });
      toast.success(`Saved ${r.data.saved} targets`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const copyFromPeriod = async () => {
    if (!copyFrom) {
      toast.error('Pick source period');
      return;
    }
    if (!window.confirm(`Copy semua targets dari ${copyFrom} ke ${period}? Existing entries di ${period} TIDAK akan di-overwrite.`)) return;
    try {
      const r = await axios.post(`${API}/scorecard/native/entries/copy-period`,
        { from_period: copyFrom, to_period: period, overwrite: false }, { headers: h });
      toast.success(`Copied ${r.data.copied}, skipped ${r.data.skipped} (already set)`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Copy failed');
    }
  };

  // Period options (next month + last 12 months) — show upcoming month
  // early so admins can pre-fill targets a few weeks in advance.
  const periodOptions = useMemo(() => {
    const now = new Date();
    const arr = [];
    // Next month first (1-2 weeks in advance prep)
    const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    arr.push({
      value: `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`,
      label: `${next.toLocaleDateString('en-MY', { month: 'short', year: 'numeric' })} (Next)`,
    });
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const v = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      arr.push({ value: v, label: d.toLocaleDateString('en-MY', { month: 'short', year: 'numeric' }) });
    }
    return arr;
  }, []);

  return (
    <div className="space-y-3" data-testid="targets-tab">
      {/* Header */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4 flex items-center gap-3 flex-wrap">
        <Calendar size={16} className="text-[#10B981]" />
        <div className="flex-1 min-w-[200px]">
          <h3 className="text-white font-semibold text-sm">Targets / Scorecards</h3>
          <p className="text-[10px] text-[#6B7280]">Set per-channel & per-staff KPI targets. Achievement live dari CRM.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#9CA3AF]">Period:</span>
          <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="targets-period-select"
            className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white text-xs rounded-md px-2 py-1.5">
            {periodOptions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}
            className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white text-xs rounded-md px-2 py-1.5">
            <option value="">— Copy from —</option>
            {periodOptions.filter(p => p.value !== period).map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <Button onClick={copyFromPeriod} disabled={!copyFrom} size="sm" variant="outline" data-testid="copy-period-btn"
            className="h-8 border-[#3B82F6]/30 text-[#3B82F6] text-xs"><Copy size={12} className="mr-1" />Copy</Button>
        </div>
      </div>

      {/* Two-pane: node picker on left, target grid on right */}
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 md:col-span-3">
          <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-2 max-h-[600px] overflow-y-auto">
            <div className="px-2 py-1.5 sticky top-0 bg-[hsl(var(--surface))] z-10">
              <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1.5">Pick Node</p>
              <div className="relative">
                <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#6B7280] pointer-events-none" />
                <Input
                  type="text"
                  value={nodeSearch}
                  onChange={(e) => setNodeSearch(e.target.value)}
                  placeholder="Search channel / staff…"
                  className="h-7 pl-7 pr-6 text-xs bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]"
                  data-testid="node-search-input"
                />
                {nodeSearch && (
                  <button
                    onClick={() => setNodeSearch('')}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-white p-0.5"
                    data-testid="node-search-clear"
                    title="Clear"
                  >
                    <X size={10} />
                  </button>
                )}
              </div>
            </div>
            {(() => {
              const q = nodeSearch.trim().toLowerCase();
              const filtered = sortedNodes
                .filter(n => n.type === 'IB' || n.type === 'STAFF')
                .filter(n => !q || n.name.toLowerCase().includes(q) || n.type.toLowerCase().includes(q));
              if (filtered.length === 0 && q) {
                return <p className="text-[10px] text-[#6B7280] px-2 py-3 italic" data-testid="node-search-empty">No match for &quot;{nodeSearch}&quot;</p>;
              }
              return filtered.map(n => (
              <div key={n.id} className={`group flex items-center gap-1 rounded-md transition ${
                selectedNodeId === n.id ? 'bg-[#10B981]/15' : 'hover:bg-[hsl(var(--surface-elevated))]'
              }`}>
                <button onClick={() => setSelectedNodeId(n.id)}
                  data-testid={`target-node-pick-${n.id}`}
                  className={`flex-1 text-left px-3 py-2 text-xs flex items-center gap-2 ${
                    selectedNodeId === n.id ? 'text-[#10B981]' : 'text-[#9CA3AF]'
                  }`}>
                  <Badge className={`text-[9px] px-1.5 py-0 ${n.type === 'IB' ? 'bg-[#F59E0B]/15 text-[#F59E0B]' : 'bg-[#8B5CF6]/15 text-[#8B5CF6]'}`}>{n.type}</Badge>
                  <span className="truncate">{n.name}</span>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setDuplicateNodeState({ open: true, node: n }); }}
                  title="Duplicate untuk channel lain"
                  data-testid={`target-node-duplicate-${n.id}`}
                  className="p-1.5 mr-1 hover:bg-[#F59E0B]/15 rounded text-[#F59E0B] opacity-0 group-hover:opacity-100 transition">
                  <Copy size={10} />
                </button>
              </div>
              ));
            })()}
            {sortedNodes.length === 0 && <p className="text-[10px] text-[#6B7280] px-2 py-3">No nodes. Run migration in Organization tab.</p>}
          </div>
        </div>

        <div className="col-span-12 md:col-span-9">
          {!selectedNode ? (
            <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-8 text-center">
              <ChevronRight size={20} className="mx-auto text-[#6B7280] mb-2" />
              <p className="text-sm text-[#9CA3AF]">Pick channel atau staff di left panel untuk set targets.</p>
            </div>
          ) : (
            <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
              <div className="bg-[hsl(var(--surface-elevated))] px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge className={selectedNode.type === 'IB' ? 'bg-[#F59E0B]/15 text-[#F59E0B]' : 'bg-[#8B5CF6]/15 text-[#8B5CF6]'}>{selectedNode.type}</Badge>
                  <p className="text-white font-semibold text-sm">{selectedNode.name}</p>
                  <p className="text-xs text-[#6B7280]">— Targets for {period}</p>
                </div>
                {dirtyCount > 0 && (
                  <Button onClick={saveDirty} disabled={saving} size="sm" data-testid="save-targets-btn"
                    className="bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white">
                    <Save size={12} className="mr-1" />Save {dirtyCount} changes
                  </Button>
                )}
              </div>
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--surface-elevated))]/60 text-[10px] text-[#9CA3AF] uppercase">
                  <tr>
                    <th className="text-left px-3 py-2 w-20">Code</th>
                    <th className="text-left px-3 py-2">Metric</th>
                    <th className="text-left px-3 py-2 w-32">Category</th>
                    <th className="text-right px-3 py-2 w-32">Target</th>
                    <th className="text-left px-3 py-2 w-16">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.filter(t => t.active !== false).map(t => {
                    const key = `${selectedNodeId}|${t.id}`;
                    const isDirty = editingTargets[key] !== undefined;
                    return (
                      <tr key={t.id} className={`border-t border-[hsl(var(--border))] ${isDirty ? 'bg-[#10B981]/5' : ''}`}
                        data-testid={`target-row-${t.id}`}>
                        <td className="px-3 py-2 font-mono text-xs text-white">{t.code}</td>
                        <td className="px-3 py-2 text-white text-xs">{t.name}</td>
                        <td className="px-3 py-2 text-xs"><Badge className="bg-[#6B7280]/15 text-[#9CA3AF] text-[10px]">{t.category}</Badge></td>
                        <td className="px-3 py-2 text-right">
                          <Input type="number" value={getCurrentTarget(t.id)}
                            onChange={(e) => setTarget(t.id, e.target.value)}
                            data-testid={`target-input-${t.id}`}
                            className={`w-28 text-right font-mono text-xs h-7 border ${isDirty ? 'border-[#10B981]/50 bg-[#10B981]/5' : 'border-[hsl(var(--border))] bg-transparent'}`} />
                        </td>
                        <td className="px-3 py-2 text-xs text-[#9CA3AF]">{t.unit || '—'}</td>
                      </tr>
                    );
                  })}
                  {templates.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-[#6B7280] text-xs">No templates. Add some in Templates tab.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <TargetNodeDuplicateModal
        open={duplicateNodeState.open}
        node={duplicateNodeState.node}
        currentPeriod={period}
        onClose={() => setDuplicateNodeState({ open: false, node: null })}
        onSaved={async () => { await loadAll(); }}
      />
    </div>
  );
}


function TargetNodeDuplicateModal({ open, node, currentPeriod, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', description: '', crm_user_id: null, crm_ib_id: null, copy_targets_from_period: '' });
  const [users, setUsers] = useState([]);
  const [ibs, setIbs] = useState([]);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');
  const h = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!open || !node) return;
    setForm({
      name: `${node.name} (copy)`,
      description: node.description || '',
      crm_user_id: null,
      crm_ib_id: null,
      copy_targets_from_period: currentPeriod || '',
    });
    if (node.type === 'STAFF') {
      axios.get(`${API}/scorecard/native/crm-users`, { headers: h }).then(r => setUsers(r.data?.users || []));
    } else if (node.type === 'IB') {
      axios.get(`${API}/scorecard/native/crm-ibs`, { headers: h }).then(r => setIbs(r.data?.ibs || []));
    }
    // eslint-disable-next-line
  }, [open, node, currentPeriod]);

  const save = async () => {
    if (!form.name?.trim()) { toast.error('Nama node baru diperlukan'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description,
        crm_user_id: node.type === 'STAFF' ? form.crm_user_id : undefined,
        crm_ib_id: node.type === 'IB' ? form.crm_ib_id : undefined,
        copy_targets_from_period: form.copy_targets_from_period || undefined,
      };
      const r = await axios.post(`${API}/scorecard/native/org-node/${node.id}/duplicate`, payload, { headers: h });
      toast.success(`Duplicated: ${r.data?.node?.name} (${r.data?.copied_entries || 0} targets copied)`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Duplicate failed');
    } finally { setSaving(false); }
  };

  if (!node) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-md" data-testid="target-node-duplicate-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Copy size={16} />
            Duplicate &quot;{node.name}&quot; untuk channel lain
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-3">
          <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-md p-3 text-[11px] text-[#F59E0B]">
            <strong>Tip:</strong> Kalau seorang staff/IB jaga lebih dari 1 channel (cth: Abang G - CIKO + Nusatrade), buat 1 node per channel supaya target & achievement boleh tracking berasingan.
          </div>
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Nama baru *</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="target-duplicate-name-input"
              placeholder="e.g. Abang G - Nusatrade"
              className="bg-transparent border-[hsl(var(--border))] text-white" autoFocus />
          </div>
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Description</label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. Channel: Revolusi Nusatrade"
              className="bg-transparent border-[hsl(var(--border))] text-white" />
          </div>
          {node.type === 'IB' && (
            <div>
              <label className="text-xs text-[#9CA3AF] mb-1 block">Link to CRM channel</label>
              <select
                value={form.crm_ib_id || ''}
                onChange={(e) => setForm({ ...form, crm_ib_id: e.target.value || null })}
                data-testid="target-duplicate-crm-ib-select"
                className="w-full bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white text-sm rounded-md px-3 py-2"
              >
                <option value="">— Pick channel lain —</option>
                {ibs.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
              <p className="text-[10px] text-[#6B7280] mt-1">Pilih channel CRM yang LAIN supaya track separately.</p>
            </div>
          )}
          {node.type === 'STAFF' && (
            <div>
              <label className="text-xs text-[#9CA3AF] mb-1 block">Link to CRM User</label>
              <select
                value={form.crm_user_id || ''}
                onChange={(e) => setForm({ ...form, crm_user_id: e.target.value || null })}
                data-testid="target-duplicate-crm-user-select"
                className="w-full bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white text-sm rounded-md px-3 py-2"
              >
                <option value="">— Not linked —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Copy targets from period</label>
            <Input value={form.copy_targets_from_period}
              onChange={(e) => setForm({ ...form, copy_targets_from_period: e.target.value })}
              placeholder="YYYY-MM (kosongkan kalau tak nak copy)"
              data-testid="target-duplicate-period-input"
              className="bg-transparent border-[hsl(var(--border))] text-white" />
            <p className="text-[10px] text-[#6B7280] mt-1">Target untuk period ini akan di-copy ke node baru.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="target-duplicate-save-btn"
            className="bg-gradient-to-r from-[#F59E0B] to-[#E63946] text-white">
            {saving ? 'Duplicating...' : 'Duplicate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
